import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, ElementRef, input, output, viewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  FwIconButtonComponent,
  FwIconComponent,
  FwPopoverComponent,
  FwProgressSpinnerComponent,
  FwTooltipDirective,
} from '@flywheel-io/vision';
import { Observable } from 'rxjs';
import { filter, map, throttleTime } from 'rxjs/operators';

import { FormatTimePipe } from '../../../../shared/pipes/format-time-pipe.pipe';
import { StorageUnitPipe } from '../../../../shared/pipes/storage-unit-pipe.pipe';
import {
  OperationCancelPopoverBodyComponent
} from '../../operation-cancel-popover-body/operation-cancel-popover-body.component';
import { OperationFeedbackItemDownloadStatus } from '../operation-feedback.enums';
import { OperationFeedbackItemData } from '../operation-feedback.interfaces';

@Component({
  host: {
    'test-id': 'operation-feedback-item',
  },
  selector: 'operation-feedback-item',
  templateUrl: './operation-feedback-item.component.html',
  styleUrls: ['./operation-feedback-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FwProgressSpinnerComponent,
    FwIconComponent,
    FwTooltipDirective,
    StorageUnitPipe,
    AsyncPipe,
    FormatTimePipe,
    FwIconButtonComponent,
    FwPopoverComponent,
    OperationCancelPopoverBodyComponent,
  ],
})
export class OperationFeedbackItem {
  readonly data = input.required<OperationFeedbackItemData>();

  readonly refresh = output<OperationFeedbackItemData>();
  readonly cancelClick = output<OperationFeedbackItemData>();

  readonly popoverTrigger = viewChild('popoverTrigger', { read: ElementRef });
  readonly popover = viewChild(FwPopoverComponent);

  protected readonly operationFeedbackItemDownloadStatus = OperationFeedbackItemDownloadStatus;
  private emaSpeed = 0;
  private emaAlpha = 0.2;
  private readonly throttleDelay = 3000;
  private readonly oneWeekMs = 7 * 24 * 60 * 60 * 1000;
  private readonly maxTitleLength = 55;
  private readonly maxDescriptionLength = 75;

  protected readonly progressValue = computed(() => {
    const value = this.data();
    let downloaded = 0;
    let total = 0;
    if (value.downloadedSize && value.totalSize) {
      downloaded = value.downloadedSize;
      total = value.totalSize;
    } else if (value.downloadedFiles && value.totalFiles) {
      downloaded = value.downloadedFiles;
      total = value.totalFiles;
    }
    const progressValueBase = total && downloaded ? downloaded / total : 0;
    return Math.ceil(progressValueBase * 100);
  });

  protected readonly currentFileShortName = computed(() =>
    this.truncateMiddle(this.generateCurrentFileShortName(this.data().currentFile), this.maxDescriptionLength),
  );

  protected readonly title = computed(() => this.truncateMiddle(this.data().title, this.maxTitleLength));

  protected readonly timeLeftData$: Observable<number> = toObservable(this.data).pipe(
    map((value) => this.calcTimeLeft(value)),
    filter((v) => Boolean(v) && v < this.oneWeekMs),
    throttleTime(this.throttleDelay, undefined, { leading: true, trailing: true }),
  );

  private calcTimeLeft(value: OperationFeedbackItemData): number {
    const now = Date.now();
    const start = new Date(value.start).getTime();
    const total = value.totalSize && value.totalFiles ? value.totalSize * value.totalFiles : value.totalSize || value.totalFiles;
    const downloaded =
      value.downloadedSize && value.downloadedFiles
        ? value.downloadedSize * value.downloadedFiles
        : value.downloadedSize || value.downloadedFiles;

    if (!total || !downloaded || downloaded <= 0) {
      return 0;
    }

    const elapsed = (now - start) / 1000;
    const instantSpeed = downloaded / elapsed;

    if (this.emaSpeed === 0) {
      this.emaSpeed = instantSpeed;
    } else {
      this.emaSpeed = this.emaAlpha * instantSpeed + (1 - this.emaAlpha) * this.emaSpeed;
    }

    const remaining = total - downloaded;
    const timeLeft = this.emaSpeed > 0 ? remaining / this.emaSpeed : 0;

    return Math.max(0, timeLeft * 1000);
  }

  protected closePopover(): void {
    if (!this.popover()?.isOpen()) {
      return;
    }
    this.popoverTrigger()?.nativeElement.click();
  }

  protected onCancelClick(): void {
    if (this.data().status === OperationFeedbackItemDownloadStatus.Progress) {
      this.popoverTrigger()?.nativeElement.click();
      return;
    }
    this.closePopover();
    this.cancelClick.emit(this.data());
  }

  protected generateCurrentFileShortName(fileName: string): string {
    if (!fileName) {
      return '';
    }

    return fileName.split('/').pop();
  }

  protected truncateMiddle(filename: string, maxLength: number): string {
    if (filename.length <= maxLength) {
      return filename;
    }

    const extIndex = filename.lastIndexOf('.');
    const extension = extIndex !== -1 ? filename.slice(extIndex) : '';
    const nameOnly = extIndex !== -1 ? filename.slice(0, extIndex) : filename;
    const charsToShow = maxLength - extension.length - 3;
    const frontChars = Math.ceil(charsToShow / 2);
    const backChars = Math.floor(charsToShow / 2);

    const truncated = nameOnly.slice(0, frontChars) + '...' + nameOnly.slice(nameOnly.length - backChars);

    return truncated + extension;
  }
}
